import { safeStorageGet, safeStorageRemove, safeStorageSet } from "@/lib/browserStorage";

const GUEST_CART_STORAGE_KEY = "releafa_guest_marketplace_cart_v1";

export type GuestMarketplaceCartItem = {
  productId: number;
  quantity: number;
  productName: string;
  price: number;
  imageUrl?: string;
  productType?: string;
  category?: string;
  dispensaryId: number;
  dispensaryName: string;
  dispensarySlug?: string;
  dispensaryAddress?: string;
  dispensaryPhone?: string;
  posCheckoutUrl?: string;
  isClaimed?: boolean;
};

function isValidGuestCartItem(item: unknown): item is GuestMarketplaceCartItem {
  const value = item as Partial<GuestMarketplaceCartItem> | null;
  return Boolean(
    value
    && Number.isInteger(value.productId)
    && Number.isInteger(value.quantity)
    && (value.quantity as number) > 0
    && typeof value.productName === "string"
    && typeof value.price === "number"
    && Number.isInteger(value.dispensaryId)
    && typeof value.dispensaryName === "string",
  );
}

/** Guest marketplace carts live only for the current browser session. */
export function readGuestMarketplaceCart(): GuestMarketplaceCartItem[] {
  try {
    const raw = safeStorageGet("session", GUEST_CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidGuestCartItem) : [];
  } catch {
    return [];
  }
}

export function writeGuestMarketplaceCart(items: GuestMarketplaceCartItem[]): boolean {
  return safeStorageSet("session", GUEST_CART_STORAGE_KEY, JSON.stringify(items));
}

export function clearGuestMarketplaceCart(): boolean {
  return safeStorageRemove("session", GUEST_CART_STORAGE_KEY);
}

/** Each guest order is limited to one dispensary, preserving fulfillment and compliance context. */
export function addGuestMarketplaceCartItem(
  current: GuestMarketplaceCartItem[],
  next: GuestMarketplaceCartItem,
): { items: GuestMarketplaceCartItem[]; replacedDispensary: boolean } {
  const sameDispensary = current.filter((item) => item.dispensaryId === next.dispensaryId);
  const replacedDispensary = current.length > 0 && sameDispensary.length !== current.length;
  const existing = sameDispensary.find((item) => item.productId === next.productId);
  const items = existing
    ? sameDispensary.map((item) => item.productId === next.productId ? { ...item, quantity: item.quantity + next.quantity } : item)
    : [...sameDispensary, next];
  return { items, replacedDispensary };
}
